import type { IconType } from "@circle-react/components/shared/Icon";
import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { isUndefined } from "lodash";
import { forwardRef } from "react";
import type {
  ComponentPropsWithoutRef,
  MouseEventHandler,
  ReactNode,
} from "react";

import { useGetSidebarLinkClass } from "../CommunityMenu/SpaceList/useGetSidebarLinkClass";
import { SidebarBadge } from "./SidebarBadge";

export interface PlatformMenuItemProps extends ComponentPropsWithoutRef<"button"> {
  ariaLabel?: string;
  badgeText?: string;
  dataTestId?: string;
  iconName: IconType;
  isActive?: boolean;
  isGray?: boolean;
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  rightSideContent?: ReactNode;
  shouldBold?: boolean;
}

export const PlatformMenuItem = forwardRef<
  HTMLButtonElement,
  PlatformMenuItemProps
>(
  (
    {
      badgeText,
      className,
      dataTestId,
      iconName,
      isActive = false,
      label,
      onClick,
      rightSideContent,
      isGray = false,
      shouldBold = false,
      ariaLabel,
      ...buttonProps
    },
    ref,
  ) => {
    const { getActiveInactiveClasses } = useGetSidebarLinkClass();

    const shouldShowBadge =
      !isUndefined(badgeText) && Number(badgeText) > 0 && !rightSideContent;
    return (
      <button
        {...buttonProps}
        ref={ref}
        type="button"
        data-testid={dataTestId}
        onClick={onClick}
        className={classNames(
          "my-px flex w-full items-center px-2 py-1.5 text-sm",
          getActiveInactiveClasses(isActive),
          className,
        )}
        aria-labelledby={label}
      >
        <Icon type={iconName} size={16} aria-hidden />
        <div className="ml-2 flex-1 text-left">
          <h3
            className={classNames("max-w-full truncate text-sm text-current", {
              "font-normal": !shouldBold,
              "font-bold": shouldBold,
            })}
          >
            {label}
          </h3>
        </div>
        {shouldShowBadge && (
          <SidebarBadge ariaLabel={ariaLabel} isGray={isGray}>
            {badgeText}
          </SidebarBadge>
        )}
        {!badgeText && rightSideContent && rightSideContent}
      </button>
    );
  },
);

PlatformMenuItem.displayName = "PlatformMenuItem";
